import { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, ArrowLeft, Home } from 'lucide-react';
import { captureError } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  routeName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Class component for error boundary logic
class RouteErrorBoundaryClass extends Component<Props & { location: string; onNavigate: (path: string) => void }, State> {
  constructor(props: Props & { location: string; onNavigate: (path: string) => void }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Error reported to Sentry below; no console leak in prod
    // Report to Sentry in production
    captureError(error, { 
      componentStack: errorInfo.componentStack,
      routeName: this.props.routeName 
    });
  }

  // Reset error state when route changes
  componentDidUpdate(prevProps: Props & { location: string }) {
    if (prevProps.location !== this.props.location && this.state.hasError) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoHome = () => {
    this.props.onNavigate('/');
  };

  handleGoDashboard = () => {
    this.props.onNavigate('/dashboard');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <CardTitle className="text-xl">Pagina kon niet worden geladen</CardTitle>
              <CardDescription className="text-base">
                Er is een fout opgetreden bij het laden van deze pagina. 
                Je kunt het opnieuw proberen of naar een andere pagina navigeren.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="rounded-lg bg-muted p-4 text-xs font-mono overflow-auto max-h-40 border">
                  <p className="text-destructive font-semibold mb-2">{this.state.error.name}: {this.state.error.message}</p>
                  {this.state.errorInfo && (
                    <details className="text-muted-foreground">
                      <summary className="cursor-pointer hover:text-foreground">Component Stack</summary>
                      <pre className="mt-2 whitespace-pre-wrap text-[10px]">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={this.handleRetry} variant="default" className="w-full">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Opnieuw proberen
                </Button>
                <Button onClick={this.handleGoBack} variant="outline" className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Ga terug
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={this.handleGoDashboard} variant="secondary" className="w-full">
                  Dashboard
                </Button>
                <Button onClick={this.handleGoHome} variant="ghost" className="w-full">
                  <Home className="mr-2 h-4 w-4" />
                  Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper to provide router hooks to class component
export function RouteErrorBoundary({ children, routeName }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <RouteErrorBoundaryClass
      location={location.pathname}
      onNavigate={navigate}
      routeName={routeName}
    >
      {children}
    </RouteErrorBoundaryClass>
  );
}
